// DesktopHomePage.js - Optimized for desktop screens with fixed image implementation
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Shield, Sparkles, Clock, Car, Star } from 'lucide-react';
import main from './images/main10.png';
import interior from './images/interiorDetail.jpg';
// Import other images
import exterior from './images/exteriorDetail.jpg';
import ceramic from './images/ceramicCoating.jpg';

// Import the new modularized Services component
import ServicesContainer from './services';

const DesktopHomePage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Full-width hero section with desktop-specific design */}
      <section className="relative h-screen overflow-hidden">
        {/* Background image - desktop optimized */}
        <div className="absolute inset-0">
          <img
            src={main}
            alt="Car detailing"
            className="w-full h-full object-cover"
          />
          {/* Gradient overlay for desktop */}
          <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-black/20 to-transparent"></div>
        </div>
        
        {/* Subtle glow effect at bottom left */}
        <div className="absolute bottom-1/4 left-0 w-1/2 h-1/2 bg-blue-500/5 blur-3xl rounded-full"></div>
        
        {/* Desktop-optimized content container */}
        <div className="absolute top-1/2 left-8 -translate-y-1/2">
          <div className="backdrop-blur-md bg-black/20 border-r border-t border-white/10 p-10 rounded-tr-3xl rounded-tl-xl max-w-2xl">
            {/* Location badge */}
            <div className="inline-flex items-center py-1 px-3 rounded-full bg-white/10 border border-white/20 mb-8">
              <span className="text-white/80 text-sm font-medium">Chicago & Surrounding Areas</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              <span className="bg-gradient-to-r from-cyan-300 to-blue-500 bg-clip-text text-transparent">Premium</span> Mobile<br/>
              Detailing Service
            </h1>
            
            {/* Feature icons in a row for desktop */}
            <div className="flex flex-wrap gap-x-6 gap-y-3 mb-8">
              <div className="flex items-center gap-2 text-white/90">
                <Shield className="h-5 w-5 text-cyan-400" />
                <span className="text-base">Premium Protection</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <Sparkles className="h-5 w-5 text-yellow-400" />
                <span className="text-base">Professional Results</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <Clock className="h-5 w-5 text-cyan-400" />
                <span className="text-base">Convenient Service</span>
              </div>
            </div>
            
            {/* Desktop CTA buttons */}
            <div className="flex gap-4">
              <Link to="/estimate" className="group flex-1">
                <button className="w-full bg-white/10 backdrop-blur-sm border border-white/20 text-white px-6 py-4 rounded-lg font-medium transition-all duration-300 overflow-hidden relative">
                  <span className="relative z-10 text-base">GET FREE ESTIMATE</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </Link>
              
              <Link to="/services" className="group flex-1">
                <button className="w-full bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-6 py-4 rounded-lg font-medium transition-all duration-300">
                  <span className="text-base">DISCOVER SERVICES</span>
                </button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Floating info cards - desktop only */}
        <div className="absolute right-8 top-1/2 transform -translate-y-1/2 w-72 space-y-4">
          {/* Card 1 */}
          <div className="backdrop-blur-md bg-white/10 p-5 rounded-xl border border-white/10 shadow-xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-full bg-cyan-500/20">
                <Car className="h-5 w-5 text-cyan-400" />
              </div>
              <h3 className="text-white font-semibold">Mobile Service</h3>
            </div>
            <p className="text-white/80 text-sm">We come to your location - home, office, or anywhere convenient for you.</p>
          </div>
          
          {/* Card 2 */}
          <div className="backdrop-blur-md bg-white/10 p-5 rounded-xl border border-white/10 shadow-xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-full bg-yellow-500/20">
                <Sparkles className="h-5 w-5 text-yellow-400" />
              </div>
              <h3 className="text-white font-semibold">Premium Packages</h3>
            </div>
            <p className="text-white/80 text-sm">From basic washes to full paint correction and ceramic coating services.</p>
          </div>
          
          {/* Card 3 */}
          <div className="backdrop-blur-md bg-white/10 p-5 rounded-xl border border-white/10 shadow-xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-full bg-cyan-500/20">
                <Star className="h-5 w-5 text-yellow-400" />
              </div>
              <h3 className="text-white font-semibold">5-Star Rated</h3>
            </div>
            <div className="flex mb-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
              ))}
            </div>
            <p className="text-white/80 text-sm">Join our hundreds of satisfied customers throughout Chicago.</p>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 right-8">
          <div className="flex items-center gap-2 text-white/80">
            <span className="text-sm tracking-widest">SCROLL</span>
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </div>
        </div>
      </section>
      
      {/* Replace the old ServicesComponent with our new ServicesContainer */}
      <ServicesContainer />

    </div>
  );
};

export default DesktopHomePage;